<script setup lang="ts">
import { watch, toRaw, ref, computed } from 'vue'
import ObjectPicker from '@/onenetwork/components/object_picker/ObjectPicker.vue'
import RealestateDetail from '@/modules/realestate/views/RealestateDetail.vue'
import { Icon } from '@iconify/vue'
import { REAL_ESTATE_CATEGORY } from '@/modules/realestate/constants/realestate.const'

interface Props {
  listing?: any
  editable: boolean
  form?: any
  newData?: any
}

const props = defineProps<Props>()
const realestate = ref<any>({})
const realestateModelValue = ref<any>([])
const showDetail = ref<boolean>(false)

// Check if current category is apartment-like (should hide width/length)
const isApartmentLike = computed(() => {
  const category = props.form?.fields?.real_estate_category?.states?.value?.code
  return [REAL_ESTATE_CATEGORY.APARTMENT, REAL_ESTATE_CATEGORY.CONDOTEL].includes(category)
})

watch(
  () => props.listing,
  (newListing) => {
    if (newListing?.real_estate_id) {
      realestate.value = {
        id: newListing?.real_estate?.id || null,
        plot_number: newListing?.plot_number || newListing?.real_estate?.plot_number || null,
        map_sheet_number:
          newListing?.map_sheet_number || newListing?.real_estate?.map_sheet_number || null,
        address: newListing?.address || newListing?.real_estate?.address || null,
        ward: newListing?.real_estate?.ward || null,
        district: newListing?.real_estate?.district || null,
        province: newListing?.real_estate?.province || null,
        width: newListing?.real_estate?.width || null,
        length: newListing?.real_estate?.length || null,
      }
    } else {
      realestate.value = {}
    }
  },
  { immediate: true },
)

const handleSelectRealestate = (data: any) => {
  realestateModelValue.value = data
  if (!data.length) {
    realestate.value = {}
    props.form?.setFieldValue('real_estate_id', null)
    props.form?.setFieldValue('plot_number', null)
    props.form?.setFieldValue('map_sheet_number', null)
    props.form?.setFieldValue('address', null)
    return
  }
  const value = toRaw(data[0])
  if (value) {
    const { item } = value
    realestate.value = {
      id: item.id,
      plot_number: item.plot_number,
      map_sheet_number: item.map_sheet_number,
      address: item.address,
      district: item.district,
      province: item.province,
      width: item.width || null,
      length: item.length || null,
    }
    props.form?.setFieldValue('real_estate_id', value.item.id)
    props.form?.setFieldValue('plot_number', value.item.plot_number)
    props.form?.setFieldValue('map_sheet_number', value.item.map_sheet_number)
    props.form?.setFieldValue('address', value.item.address)
  }
}

const removeRealestate = () => {
  realestateModelValue.value = []
  realestate.value = {}
  props.form?.setFieldValue('real_estate_id', null)
  props.form?.setFieldValue('plot_number', null)
  props.form?.setFieldValue('map_sheet_number', null)
  props.form?.setFieldValue('address', null)
}

const real_estate_id_update = computed(() => {
  return props.newData &&
    +props.form?.fields?.real_estate_id?.states?.value !== +props.newData?.real_estate_id
})

const plot_number_update = computed(() => {
  return props.newData &&
    props.form?.fields?.plot_number?.states?.value !== props.newData?.plot_number
})

const map_sheet_number_update = computed(() => {
  return props.newData &&
    props.form?.fields?.map_sheet_number?.states?.value !== props.newData?.map_sheet_number
})

const address_update = computed(() => {
  return props.newData && props.form?.fields?.address?.states?.value !== props.newData?.address
})
</script>

<template>
  <div class="flex flex-col w-full pt-3 gap-y-5">
    <InputText type="text" hidden name="real_estate_id" />
    <InputText type="text" hidden name="plot_number" />
    <InputText type="text" hidden name="map_sheet_number" />
    <InputText type="text" hidden name="address" />
    <div class="w-full md:flex-3">
      <div class="mb-3 gap-3" v-if="props.editable">
        <div class="flex w-full">
          <object-picker
            :modelValue="realestateModelValue"
            api-url="real_estate"
            query-field="keyword"
            key_field="id"
            display_field="address"
            placeholder="Chọn tài sản liên kết"
            @update:selected="handleSelectRealestate"
          />
        </div>
        <span class="font-italic text-[12px] text-red-500">
          {{ props.form?.fields?.real_estate_id?.states?.error?.message }}
        </span>
      </div>
      <Divider />
      <div
        :class="`listing-assets flex flex-col w-full ${props.newData && real_estate_id_update ? '!bg-red-100 !text-red-800 mb-1' : ''}`"
      >
        <div class="flex flex-1 mb-3">
          <div class="flex-1">Mã BDS:</div>
          <div class="flex-2">
            <badge severity="secondary" class="font-bold"> #{{ realestate?.id }} </badge>
          </div>
        </div>
        <div class="flex flex-1 mb-3">
          <div class="flex-1">Khu vực:</div>
          <div class="flex-2">
            <span class="font-bold">
              {{ realestate?.ward?.name }}
              {{ ' - ' }}
              {{ realestate?.province?.name }}
            </span>
          </div>
        </div>
        <div class="flex flex-1 mb-3">
          <div class="flex-1">Địa chỉ:</div>
          <div class="flex-2">
            <div>
              <span :class="`font-italic ${(!real_estate_id_update && address_update) ? '!bg-red-100 !text-red-800' : ''}`">
                {{ realestate?.address || '--' }}
              </span>
            </div>
            <div v-if="!real_estate_id_update && address_update">
              <span :class="`font-italic ${(!real_estate_id_update && address_update) ? '!bg-green-100 !text-green-800' : ''}`">
                {{ props.newData?.address || '--' }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex flex-1 mb-3">
          <div class="flex-1">Tờ thửa:</div>
          <div class="flex-2">
            <div>
              <span class="font-italic">
                Thửa <span :class="`font-bold ${!real_estate_id_update && plot_number_update ? '!bg-red-100 !text-red-800' : ''}`">
                  {{ realestate?.plot_number || '--' }}
                </span>
                {{ ' - ' }}
                Tờ bản đồ <span :class="`font-bold ${!real_estate_id_update && map_sheet_number_update ? '!bg-red-100 !text-red-800' : ''}`">
                  {{ realestate?.map_sheet_number || '--' }}
                </span>
              </span>
            </div>
            <div v-if="props.newData && !real_estate_id_update && (plot_number_update || map_sheet_number_update)">
              <span class="font-italic">
                Thửa <span :class="`font-bold ${!real_estate_id_update && plot_number_update ? '!bg-green-100 !text-green-800' : ''}`">
                  {{ realestate?.plot_number || '--' }}
                </span>
                {{ ' - ' }}
                Tờ bản đồ <span :class="`font-bold ${!real_estate_id_update && map_sheet_number_update ? '!bg-green-100 !text-green-800' : ''}`">
                  {{ props.newData?.map_sheet_number || '--' }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <!-- Chiều rộng / chiều dài từ real_estate (ẩn cho căn hộ) -->
        <div class="flex flex-1 mb-3" v-if="!isApartmentLike && (realestate?.width || realestate?.length)">
          <div class="flex-1">Kích thước:</div>
          <div class="flex-2">
            <span class="font-bold">
              <template v-if="realestate?.width">{{ realestate.width }}m</template>
              <template v-if="realestate?.width && realestate?.length"> × </template>
              <template v-if="realestate?.length">{{ realestate.length }}m</template>
            </span>
          </div>
        </div>
        <div class="detail-btn">
          <div
            class="flex items-center gap-[3px] hover:text-[#0000FF]"
            @click="
              () => {
                showDetail = true
              }
            "
            v-if="realestate?.id"
          >
            <Icon icon="bi:info-circle-fill" /> chi tiết
          </div>
          <div
            class="flex items-center gap-[3px] hover:text-[#FF0000]"
            @click="removeRealestate"
            v-if="props.editable && realestate?.id"
          >
            <Icon icon="mingcute:close-circle-line" /> gỡ
          </div>
        </div>
      </div>
      <template
        v-if="real_estate_id_update"
      >
        <div class="listing-assets flex flex-col w-full !bg-green-100 !text-green-800">
          <div class="flex flex-1 mb-3">
            <div class="flex-1">Mã BDS:</div>
            <div class="flex-2">
              <badge severity="secondary" class="font-bold">
                #{{ props.newData?.real_estate?.id }}
              </badge>
            </div>
          </div>
          <div class="flex flex-1 mb-3">
            <div class="flex-1">Khu vực:</div>
            <div class="flex-2">
              <span class="font-bold">
                {{ props.newData?.real_estate?.ward?.name }}
                <template v-if="props.newData?.real_estate?.district?.name">{{ ' - ' }}{{ props.newData.real_estate.district.name }}</template>
                {{ ' - ' }}
                {{ props.newData?.real_estate?.province?.name }}
              </span>
            </div>
          </div>
          <div class="flex flex-1 mb-3">
            <div class="flex-1">Địa chỉ:</div>
            <div class="flex-2">
              <span class="font-italic">{{ props.newData?.real_estate?.address || '--' }}</span>
            </div>
          </div>
          <div class="flex flex-1 mb-3">
            <div class="flex-1">Tờ thửa:</div>
            <div class="flex-2">
              <span class="font-italic">
                Thửa
                <span class="font-bold">{{ props.newData?.real_estate?.plot_number || '--' }}</span>
                {{ ' - ' }}
                Tờ bản đồ
                <span class="font-bold">{{
                  props.newData?.real_estate?.map_sheet_number || '--'
                }}</span>
              </span>
            </div>
          </div>
          <!-- Kích thước từ real_estate mới (ẩn cho căn hộ) -->
          <div
            class="flex flex-1 mb-3"
            v-if="!isApartmentLike && (props.newData?.real_estate?.width || props.newData?.real_estate?.length)"
          >
            <div class="flex-1">Kích thước:</div>
            <div class="flex-2">
              <span class="font-bold">
                <template v-if="props.newData?.real_estate?.width">{{ props.newData.real_estate.width }}m</template>
                <template v-if="props.newData?.real_estate?.width && props.newData?.real_estate?.length"> × </template>
                <template v-if="props.newData?.real_estate?.length">{{ props.newData.real_estate.length }}m</template>
              </span>
            </div>
          </div>
          <div class="detail-btn">
            <div
              class="flex items-center gap-[3px] hover:text-[#0000FF]"
              @click="
                () => {
                  showDetail = true
                }
              "
              v-if="props.newData?.real_estate?.id"
            >
              <Icon icon="bi:info-circle-fill" /> chi tiết
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
  <Drawer v-model:visible="showDetail" header="Thông tin BDS" position="right">
    <div class="drawer-80vw">
      <realestate-detail :id="realestate.id" />
    </div>
  </Drawer>
</template>

<style scoped></style>
